import React, { Component } from 'react'
import PropTypes from 'prop-types'

import { MultiSelect } from '@blueprintjs/select'
import { Button } from '@blueprintjs/core'
import { cloneDeep } from 'lodash'

import styles from './multiSelectInput.module.scss'
import classNames from 'classnames'

class MultiSelectInput extends Component {
    static propTypes = {
        /**
         * Render a full width input
         */
        fullWidth: PropTypes.bool,
        /**
         * Render a large input
         * */
        large: PropTypes.bool,
        /**
         * Triggered when an item is selected
         */
        onItemSelect: PropTypes.func,
        /**
         * Triggered when an item is deselected
         */
        onDeselectItem: PropTypes.func,
        /**
         * Triggered when all selected items are removed from the selection
         */
        onClearAll: PropTypes.func,
    }

    static defaultProps = {
        fullWidth: false,
        large: false,
    }

    state = {
        items: [],
        selectedItems: [],
    }

    inputRef = React.createRef()

    componentDidMount() {
        const { items } = this.props

        this.setState({ items })
    }

    componentDidUpdate() {
        // This was needed because blueprintjs is not passing the readOnly prop to the input
        // and because of that there is not way to make the input read only
        const isReadOnly = this.inputRef.current.inputElement.getAttribute(
            'readonly'
        )

        if (this.inputRef.current && !isReadOnly) {
            this.inputRef.current.inputElement.setAttribute('readonly', true)
        }
    }

    handleTagRemove = (_tag, index) => {
        this.deselectItem(index)
    }

    getSelectedItemIndex(item) {
        const { selectedItems } = this.state

        return selectedItems.indexOf(item)
    }

    isItemSelected(item) {
        return this.getSelectedItemIndex(item) !== -1
    }

    selectItem(selectedItem) {
        const { selectedItems, items } = this.state
        const { onItemSelect } = this.props

        const newItems = items.filter((item) => item.id !== selectedItem.id)

        this.setState({
            selectedItems: [
                ...cloneDeep(selectedItems),
                cloneDeep(selectedItem),
            ],
            items: newItems,
        })
        onItemSelect(selectedItem)
    }

    deselectItem(index) {
        const { selectedItems, items } = this.state
        const { onDeselectItem } = this.props

        const updatedSelectedItems = cloneDeep(selectedItems)
        const deselectedItem = updatedSelectedItems.splice(index, 1)[0]

        this.setState({
            selectedItems: updatedSelectedItems,
            items: [items, deselectedItem].sort((a, b) => a.id - b.id),
        })
        onDeselectItem(deselectedItem)
    }

    handleItemSelect = (item) => {
        if (!this.isItemSelected(item)) {
            this.selectItem(item)
        } else {
            this.deselectItem(this.getSelectedItemIndex(item))
        }
    }

    handleClear = () => {
        const { items, selectedItems } = this.state
        const { onClearAll } = this.props

        this.setState({
            selectedItems: [],
            items: [...cloneDeep(items), ...cloneDeep(selectedItems)].sort(
                (a, b) => a.id - b.id
            ),
        })
        onClearAll()
    }

    render() {
        const { items, selectedItems } = this.state

        const {
            fullWidth,
            large,
            onItemSelect,
            onDeselectItem,
            onClearAll,
            tagInputProps,
            ...other
        } = this.props

        const clearButton =
            selectedItems.length > 0 ? (
                <Button
                    icon="cross"
                    minimal={true}
                    onClick={this.handleClear}
                />
            ) : undefined

        return (
            <MultiSelect
                {...other}
                items={items}
                onItemSelect={this.handleItemSelect}
                popoverProps={{ minimal: true }}
                tagInputProps={{
                    onRemove: this.handleTagRemove,
                    rightElement: clearButton,
                    tagProps: {
                        intent: 'primary',
                        minimal: true,
                        large: large,
                    },
                    ref: this.inputRef,
                    readOnly: true,
                    ...tagInputProps,
                }}
                selectedItems={selectedItems}
                className={classNames(styles.multiSelectInput, {
                    [styles.largeInput]: large,
                    [styles.fullWidth]: fullWidth,
                })}
            />
        )
    }
}

export default MultiSelectInput
